import { useRef, useEffect } from "react";
import { JSONContent } from "@tiptap/react";
import { useForm } from "@inertiajs/react";
import debounce from "lodash/debounce";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Editor } from "@/components/Editor";
import usePage from "@/hooks/usePage";
import useContentEntityStore from "@/hooks/useContentEntityStore";
import { Button } from "@/components/ui/button";

type FormData = {
  content_entry: {
    title: string;
    content_entry_parts_attributes: {
      id: number;
      text: string;
      text_json: JSONContent;
    };
  };
};

export default function ContentEntry() {
  const { setProcessing } = useContentEntityStore();
  const { props } = usePage<
    AdminUsePageProps & { contentEntry: ContentEntry }
  >();
  const { data, setData, put, processing } = useForm<FormData>({
    content_entry: {
      title: props.contentEntry.title,
      content_entry_parts_attributes: {
        id: props.contentEntry.contentEntryParts[0].id,
        text: props.contentEntry.contentEntryParts[0].text,
        text_json: props.contentEntry.contentEntryParts[0].textJson,
      },
    },
  });
  const prevTitle = useRef<string>(data.content_entry.title);
  const prevText = useRef<string>(
    data.content_entry.content_entry_parts_attributes.text
  );
  const debouncedPut = debounce(() => {
    put(`/admin/${props.publication.slug}/content/${props.contentEntry.slug}`);
  }, 100);

  const onSave = () => {
    debouncedPut();
    setProcessing(true);

    prevTitle.current = data.content_entry.title;
    prevText.current = data.content_entry.content_entry_parts_attributes.text;

    setTimeout(() => setProcessing(false), 1000);
  };

  useEffect(() => {
    let timeout: any;

    if (processing) return;

    if (
      data.content_entry.title !== prevTitle.current ||
      data.content_entry.content_entry_parts_attributes.text !==
        prevText.current
    ) {
      timeout = setTimeout(onSave, 1000);
    } else {
      clearTimeout(timeout);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    data.content_entry.title,
    data.content_entry.content_entry_parts_attributes.text,
    processing,
  ]);

  return (
    <Tabs defaultValue="editor">
      <div className="flex items-center bg-white border-b border-gray-200 h-12 sticky top-0 px-2 z-10">
        <TabsList>
          <TabsTrigger value="editor">Editor</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <Button variant="secondary" size="xs" className="ml-auto">
          Publish
        </Button>
      </div>
      <TabsContent value="editor">
        <div id="editor-area" className="w-[900px] mx-auto overflow-hidden">
          <Editor
            text={data.content_entry.content_entry_parts_attributes.text}
            onChange={({ title, text, text_json }) => {
              setData({
                ...data,
                content_entry: {
                  ...data.content_entry,
                  title,
                  content_entry_parts_attributes: {
                    ...data.content_entry.content_entry_parts_attributes,
                    text,
                    text_json,
                  },
                },
              });
            }}
            onCmdSave={onSave}
          />
        </div>
      </TabsContent>
      <TabsContent value="settings">Entry settings (cover, etc).</TabsContent>
    </Tabs>
  );
}
